<template>
  <main class="account-page">
    <div class="container account-page__inner">
      <button type="button" class="account-page__back" @click="goBack">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M5 12H19" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>بازگشت</span>
      </button>

      <header class="account-page__header">
        <p class="account-page__eyebrow">حساب کاربری</p>
        <h1 class="account-page__title">پروفایل</h1>
        <p class="account-page__subtitle">اطلاعات حساب و آواتار cocouni شما</p>
      </header>

      <section class="account-page__card">
        <div class="account-page__avatar" :class="{ 'account-page__avatar--image': !!avatarSrc }">
          <img
            v-if="avatarSrc"
            :src="avatarSrc"
            alt="آواتار"
            class="account-page__avatar-img"
          />
          <template v-else>
            <i v-if="!initials" class="bi bi-person-fill account-page__avatar-icon"></i>
            <template v-else>{{ initials }}</template>
          </template>
        </div>

        <div class="account-page__avatars">
          <h2 class="account-page__avatars-title">انتخاب آواتار</h2>
          <p class="account-page__avatars-hint">یکی از نارگیل‌های cocouni رو برای پروفایلت انتخاب کن.</p>
          <div class="account-page__avatars-grid" role="listbox" aria-label="آواتارها">
            <button
              v-for="avatar in avatars"
              :key="avatar.id"
              type="button"
              class="account-page__avatar-option"
              :class="{ 'account-page__avatar-option--active': selectedId === avatar.id }"
              :aria-selected="selectedId === avatar.id"
              :title="avatar.label"
              :disabled="saving"
              @click="selectAvatar(avatar.id)"
            >
              <img :src="avatar.src" :alt="avatar.label" />
            </button>
          </div>
          <p v-if="saveMessage" class="account-page__save-msg" :class="{ 'is-error': saveError }">
            {{ saveMessage }}
          </p>
        </div>

        <div class="account-page__username">
          <label class="account-page__username-label" for="username">نام کاربری</label>
          <p class="account-page__avatars-hint">این نام در جامعه یادگیری و جدول امتیازات دیده می‌شود، نه شماره موبایل. ۳ تا ۲۴ کاراکتر، با حرف انگلیسی شروع شود.</p>
          <div class="account-page__username-row">
            <span class="account-page__username-prefix">@</span>
            <input
              id="username"
              v-model.trim="usernameDraft"
              class="account-page__username-input"
              maxlength="24"
              spellcheck="false"
              autocapitalize="off"
              autocomplete="username"
              placeholder="coco_abc123"
              :disabled="savingUsername"
            />
            <button
              type="button"
              class="account-page__username-save"
              :disabled="savingUsername || !usernameDirty"
              @click="saveUsername"
            >
              {{ savingUsername ? "..." : "ذخیره" }}
            </button>
          </div>
        </div>

        <dl class="account-page__details">
          <div class="account-page__row">
            <dt>نام کامل</dt>
            <dd>{{ user?.full_name || "—" }}</dd>
          </div>
          <div class="account-page__row">
            <dt>شماره موبایل</dt>
            <dd>{{ user?.phone_number || "—" }}</dd>
          </div>
        </dl>
      </section>
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { updateMe } from "@/api/users";
import { useAuth } from "@/composables/useAuth";
import { COCO_AVATARS, resolveAvatarSrc } from "@/constants/avatars";
import { handleError } from "@/services/errorHandler";

const router = useRouter();
const { user, init, fetchCurrentUser } = useAuth();
const avatars = COCO_AVATARS;
const saving = ref(false);
const savingUsername = ref(false);
const usernameDraft = ref("");
const saveMessage = ref<string | null>(null);
const saveError = ref(false);

const usernameDirty = computed(() => {
  const current = typeof user.value?.username === "string" ? user.value.username : "";
  return usernameDraft.value.trim().toLowerCase() !== current.toLowerCase();
});

const selectedId = computed(() => {
  const value = user.value?.avatar_url;
  return typeof value === "string" ? value : null;
});

const avatarSrc = computed(() => resolveAvatarSrc(selectedId.value));

const initials = computed(() => {
  const name = user.value?.full_name?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const handle = typeof user.value?.username === "string" ? user.value.username : "";
  if (handle) return handle.slice(0, 2).toUpperCase();
  return "";
});

async function selectAvatar(id: string) {
  if (saving.value || selectedId.value === id) return;
  saving.value = true;
  saveMessage.value = null;
  saveError.value = false;
  try {
    await updateMe({ avatar_url: id });
    await fetchCurrentUser();
    saveMessage.value = "آواتار ذخیره شد.";
  } catch (e) {
    saveError.value = true;
    saveMessage.value = handleError(e, "Profile.selectAvatar");
  } finally {
    saving.value = false;
  }
}

async function saveUsername() {
  if (!usernameDirty.value || savingUsername.value) return;
  savingUsername.value = true;
  saveMessage.value = null;
  saveError.value = false;
  try {
    await updateMe({ username: usernameDraft.value.trim().toLowerCase() });
    await fetchCurrentUser();
    saveMessage.value = "نام کاربری ذخیره شد.";
  } catch (e) {
    saveError.value = true;
    saveMessage.value = handleError(e, "Profile.saveUsername");
  } finally {
    savingUsername.value = false;
  }
}

function goBack() {
  router.push("/dashboard");
}

onMounted(async () => {
  await init();
  await fetchCurrentUser();
});

watch(
  () => user.value?.username,
  (value) => {
    if (typeof value === "string") usernameDraft.value = value;
  },
  { immediate: true }
);
</script>

<style scoped>
.account-page {
  --color-primary: #6D5EF8;
  --color-bg: #FCF9F6;
  --color-card: #FFFFFF;
  --color-border: #ECE4D9;
  --color-text: #4C372A;
  --color-muted: #8C8176;

  min-height: 100vh;
  background: var(--color-bg);
  color: var(--color-text);
  font-family: Vazirmatn, system-ui, "Segoe UI", Tahoma, sans-serif;
  padding: 56px 0 80px;
  direction: rtl;
}

.account-page__inner {
  max-width: 640px;
}

.account-page__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--color-card);
  border: 1px solid var(--color-border);
  color: var(--color-text);
  font-size: 14px;
  font-weight: 500;
  padding: 9px 18px;
  border-radius: 999px;
  cursor: pointer;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(76, 55, 42, 0.06);
}

.account-page__back:hover {
  background: #f5f3ff;
  border-color: var(--color-primary);
}

.account-page__header {
  margin-bottom: 28px;
}

.account-page__eyebrow {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #6A4328;
}

.account-page__title {
  margin: 0 0 8px;
  font-size: 32px;
  font-weight: 700;
}

.account-page__subtitle {
  margin: 0;
  color: var(--color-muted);
}

.account-page__card {
  background: var(--color-card);
  border: 1px solid var(--color-border);
  border-radius: 18px;
  padding: 28px;
  box-shadow: 0 8px 24px rgba(76, 55, 42, 0.06);
}

.account-page__avatar {
  width: 88px;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #6D5EF8 0%, #865FEA 100%);
  color: #fff;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 22px;
  overflow: hidden;
  border: 2px solid #ECE4D9;
}

.account-page__avatar--image {
  background: #FFFDF8;
}

.account-page__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.account-page__avatar-icon {
  font-size: 2rem;
  line-height: 1;
}

.account-page__avatars {
  margin-bottom: 22px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--color-border);
}

.account-page__avatars-title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
}

.account-page__avatars-hint {
  margin: 0 0 14px;
  font-size: 13px;
  color: var(--color-muted);
}

.account-page__avatars-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.account-page__avatar-option {
  aspect-ratio: 1;
  border: 2px solid #ECE4D9;
  border-radius: 16px;
  background: #FFFDF8;
  padding: 4px;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.account-page__avatar-option img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.account-page__avatar-option:hover:not(:disabled) {
  border-color: #c4b5fd;
  transform: translateY(-1px);
}

.account-page__avatar-option--active {
  border-color: #6D5EF8;
  box-shadow: 0 0 0 3px rgba(109, 94, 248, 0.2);
}

.account-page__avatar-option:disabled {
  opacity: 0.7;
  cursor: wait;
}

.account-page__save-msg {
  margin: 12px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #3F8F4E;
}

.account-page__save-msg.is-error {
  color: #C45C5C;
}

.account-page__username {
  margin-bottom: 22px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--color-border);
}

.account-page__username-label {
  display: block;
  margin-bottom: 6px;
  font-size: 16px;
  font-weight: 700;
}

.account-page__username-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.account-page__username-prefix {
  color: var(--color-muted);
  font-weight: 700;
}

.account-page__username-input {
  flex: 1;
  min-width: 0;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 10px 12px;
  font-family: inherit;
  font-size: 15px;
  color: var(--color-text);
  background: #fffdf8;
}

.account-page__username-save {
  border: none;
  border-radius: 12px;
  padding: 10px 14px;
  background: #6d5ef8;
  color: #fff;
  font-family: inherit;
  font-weight: 700;
  cursor: pointer;
}

.account-page__username-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.account-page__details {
  margin: 0;
}

.account-page__row {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid var(--color-border);
}

.account-page__row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.account-page__row dt {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-muted);
}

.account-page__row dd {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
}

@media (max-width: 576px) {
  .account-page__avatars-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .account-page__row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}
</style>
